import React from "react";
import { Function0, Function1 } from "lodash";
import fp from "lodash/fp";
import { Modal, Row, Col, ModalProps } from "react-bootstrap";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import {
	Field,
	formValueSelector,
	InjectedFormProps,
	reduxForm,
} from "redux-form";

import { Button, CloseButton, SelectFormGroup } from "components";
import { getFullName } from "helpers";
import { reassignMerchantsService } from "services/systemSetup/reassignMerchants.service";

import styles from "./reassignMerchants.module.scss";

const FORM_NAME = "reassignMerchants";

type ReassingMerchantsFormData = {
	newAgent: { id: string };
};

type ReassignMerchantsModalProps = {
	show: boolean;
	onHide: Function0<void>;
	dialogAs?: ModalProps["dialogAs"];
	backdrop: any;
	oldAgent: {
		id: string;
		firstName: string;
		lastName: string;
	};
	onReassign: Function1<{ newAgentId: string }, Promise<void> | void>;
};

type ReassignMerchantModalType = React.FC<
	InjectedFormProps<ReassingMerchantsFormData, ReassignMerchantsModalProps> &
		ReassignMerchantsModalProps
>;

const ReassignMerchantsModal: ReassignMerchantModalType = ({
	show,
	onHide,
	dialogAs,
	backdrop,
	oldAgent,
	handleSubmit,
	onReassign,
}) => {
	const { isLoading, data: agents } = useQuery(
		"newAgentsForReassign",
		() =>
			reassignMerchantsService.getNewAgents(oldAgent.id).then(
				fp.sortBy((agent: any) => {
					const fullName = getFullName(agent);
					return fullName?.toLowerCase();
				}),
			),
		{ cacheTime: 0 },
	);

	const handleReassign = async (form: ReassingMerchantsFormData) => {
		try {
			await onReassign({ newAgentId: form?.newAgent?.id });
		} finally {
			onHide();
		}
	};

	const selector = formValueSelector(FORM_NAME);
	const getNewAgent = state => selector(state, "newAgent");
	const newAgent = useSelector(getNewAgent);

	return (
		<Modal
			show={show}
			onHide={onHide}
			animation={false}
			centered
			dialogAs={dialogAs}
			backdrop={backdrop}
			size="lg"
		>
			<Modal.Header className={styles.header}>
				<Modal.Title className={styles.title}>
					Please select new primary agent for merchant(s)
					<br />
					managed by {getFullName(oldAgent)}
				</Modal.Title>
				<CloseButton onClick={onHide} />
			</Modal.Header>
			<Modal.Body className={styles.body}>
				<Row
					as="form"
					id={FORM_NAME}
					onSubmit={handleSubmit(handleReassign)}
				>
					<Col md={{ span: 6, offset: 3 }}>
						<Field
							component={SelectFormGroup}
							name="newAgent"
							label="Primary Agent"
							options={agents}
							isLoading={isLoading}
							getOptionValue={agent => agent}
							getOptionLabel={getFullName}
							labelSize={{ sm: 12 }}
							controlSize={{ sm: 12 }}
							disableSkeleton
						/>
					</Col>
				</Row>
			</Modal.Body>
			<Modal.Footer className={styles.footer}>
				<Button
					type="submit"
					form={FORM_NAME}
					className={styles.submitBtn}
					value="Save"
					disabled={!newAgent}
				/>
			</Modal.Footer>
		</Modal>
	);
};

export default reduxForm<
	ReassingMerchantsFormData,
	ReassignMerchantsModalProps
>({
	form: FORM_NAME,
})(ReassignMerchantsModal);
